/**
 * The mark-state vocabulary for every attendance surface — program plan §11's
 * binding table, in one place so the marking screen and the inbox cannot drift
 * into two dialects of the same state.
 *
 * Same rule as its money-side sibling (`billing/billingVocabulary.tsx`): colour
 * is never the only signal. Every state carries an ink label; green and amber
 * appear as a dot, a ring, or a wash behind ink text, never as small coloured
 * text. Brick red is reserved for destructive buttons — a waived student is not
 * an error.
 *
 * `AMBER` and `TNUM_SX` are imported from the billing vocabulary rather than
 * redefined. The two surfaces render the same amber wash for "this changes the
 * money", and the first draft of the billing file already proved that hand-typed
 * rgba drifts within one file, let alone across two.
 */
import React from 'react';
import { Box, Chip } from '@mui/material';

import { getDateKeyInTimezone } from '@shared/classCodeContract.js';
import { C } from '@/components/adaptive-picker/adaptiveTokens';
import { AMBER, TNUM_SX } from '@/components/admin/billing/billingVocabulary';
import type {
  AttendanceLine,
  AttendanceSessionSummary,
  MarkStatus,
  ZoomParticipantSnapshot,
} from '@/services/classSessionsApi';

export { AMBER, TNUM_SX };

const DEFAULT_TZ = 'Asia/Singapore';

/** The uppercase group label both attendance lists use above a stack of rows. */
export const EYEBROW_SX = {
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: C.inkMuted,
};

/** "Wed 5 Aug" — the session header and every row that names another date. */
export function formatSessionDay(
  iso: string | null | undefined,
  timezone: string = DEFAULT_TZ
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: timezone,
  })
    .format(date)
    .replace(',', '');
}

/**
 * "4:30pm". The session DTO carries a start instant and no duration, so the
 * header shows when the class began and never invents an end time.
 */
export function formatSessionTime(
  iso: string | null | undefined,
  timezone: string = DEFAULT_TZ
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  })
    .format(date)
    .replace(' ', '')
    .toLowerCase();
}

/**
 * The `YYYY-MM-DD` a session belongs to, in ITS OWN timezone — the grouping key
 * for every day-grouped list. Read in the class's zone rather than the browser's
 * so a 9pm session never lands under tomorrow for an admin who is travelling.
 */
export function sessionDayKey(
  iso: string,
  timezone: string = DEFAULT_TZ
): string {
  return getDateKeyInTimezone(new Date(iso), timezone) ?? iso.slice(0, 10);
}

/** Today's key in the same calendar, for "Today" / "Yesterday" headings. */
export function todayDayKey(timezone: string = DEFAULT_TZ): string {
  return getDateKeyInTimezone(new Date(), timezone) ?? '';
}

/**
 * A day-group heading: "Today · Wed 5 Aug". The relative word is what makes a
 * long list scannable at a glance; the date is always spelled out beside it so
 * nobody has to work out which day "Yesterday" was.
 *
 * Parsed at noon UTC so the key renders as the same calendar day in any zone
 * the admin's browser happens to be in.
 */
export function formatDayHeading(
  dayKey: string,
  today: string = todayDayKey()
): string {
  const label = formatSessionDay(`${dayKey}T12:00:00.000Z`);
  if (dayKey === today) return `Today · ${label}`;
  if (dayKey === previousDayKey(today)) return `Yesterday · ${label}`;
  return label;
}

/** The calendar day before `dayKey`, as a key. */
export function previousDayKey(dayKey: string): string {
  const at = Date.parse(`${dayKey}T12:00:00.000Z`);
  if (Number.isNaN(at)) return dayKey;
  return new Date(at - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** The calendar day after `dayKey`, as a key. */
export function nextDayKey(dayKey: string): string {
  const at = Date.parse(`${dayKey}T12:00:00.000Z`);
  if (Number.isNaN(at)) return dayKey;
  return new Date(at + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Which inbox screen a class page was opened from
// ---------------------------------------------------------------------------

/** The inbox's three views over one ledger. A date in the URL IS the day view. */
export type InboxViewKey = 'unmarked' | 'completed' | 'day';

/**
 * The class page is reached from several places, so "back" has to be told which
 * screen to rebuild rather than guessing — and one of the inbox's views carries
 * a date, so naming the view alone is not enough. Landing an admin on To mark
 * after they opened a class from 5 Aug means re-picking the date on every single
 * row, which is the whole day's work done twice.
 *
 * It travels as ONE token — `completed`, a `YYYY-MM-DD` day, or absent for the
 * default To-mark list — because the views are mutually exclusive and a day key
 * is self-identifying, so a second companion param could only ever disagree.
 *
 * A token, never a return URL: the back button rebuilds `/admin/attendance`
 * itself, so a hand-edited query string can reach another attendance view and
 * nothing else.
 */
export const INBOX_ORIGIN_PARAM = 'from';

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The token for the inbox view currently showing. Null for To mark: that is
 * where the back button lands with no token at all, so spelling it out would
 * only add a param that changes nothing.
 */
export function inboxOriginToken(
  view: InboxViewKey,
  selectedDate: string
): string | null {
  if (view === 'day') {
    return DAY_KEY_PATTERN.test(selectedDate) ? selectedDate : null;
  }
  return view === 'completed' ? 'completed' : null;
}

/** The inbox URL a token names; the default list for a missing or junk one. */
export function inboxReturnPath(token: string | null | undefined): string {
  if (token === 'completed') return '/admin/attendance?view=completed';
  if (token && DAY_KEY_PATTERN.test(token)) {
    return `/admin/attendance?date=${token}`;
  }
  return '/admin/attendance';
}

/** "5 Aug, 9:12pm" — the audit footer and the per-mark change line. */
export function formatMarkTimestamp(
  iso: string | null | undefined,
  timezone: string = DEFAULT_TZ
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: timezone,
  }).format(date);
  return `${day}, ${formatSessionTime(iso, timezone)}`;
}

// ---------------------------------------------------------------------------
// Mark options — the overlay's menu, with money-consequence subtitles
// ---------------------------------------------------------------------------

export interface MarkOption {
  status: MarkStatus;
  label: string;
  /** Why an admin would pick it, in money terms (§11). */
  subtitle: string;
}

/** Enrolled and visiting lines. Order is the overlay's order. */
export const ENROLLED_MARK_OPTIONS: MarkOption[] = [
  {
    status: 'present-billed',
    label: 'Present · billed',
    subtitle: 'counts for pay & uses a paid slot',
  },
  {
    status: 'absent-billed',
    label: 'Absent · billed',
    subtitle: 'still billed — recording provided',
  },
  {
    status: 'waived',
    label: 'Waived',
    subtitle: 'no charge, no slot — reason required',
  },
];

/**
 * Trial lines. Two options, and neither touches a billing cycle: a trial
 * student pays nothing and the teacher earns the flat trial rate for an
 * attended one.
 */
export const TRIAL_MARK_OPTIONS: MarkOption[] = [
  {
    status: 'trial-attended',
    label: 'Trial attended',
    subtitle: 'counts toward the flat trial rate',
  },
  {
    status: 'trial-no-show',
    label: 'No-show',
    subtitle: 'no charge, no pay',
  },
];

export function markOptionsFor(line: AttendanceLine): MarkOption[] {
  return line.kind === 'trial' ? TRIAL_MARK_OPTIONS : ENROLLED_MARK_OPTIONS;
}

// ---------------------------------------------------------------------------
// State chips
// ---------------------------------------------------------------------------

/**
 * Filled dot = present, ring = absent. Same green, same money meaning; the
 * difference is shape, not hue, so it survives a colour-blind read.
 *
 * Exported because it is also the carrier for green on non-chip surfaces: green
 * is a 3:1 token, so it may tint a dot but never the words beside it (DESIGN.md
 * decisions 2026-07-25).
 */
export const StateDot: React.FC<{
  ring?: boolean;
  tone?: 'green' | 'cobalt' | 'ghost';
}> = ({ ring = false, tone = 'green' }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
      bgcolor:
        ring || tone === 'ghost'
          ? 'transparent'
          : tone === 'cobalt'
            ? C.cobalt
            : C.green,
      border:
        ring || tone === 'ghost'
          ? `2px solid ${tone === 'ghost' ? C.inkMuted : tone === 'cobalt' ? C.cobalt : C.green}`
          : 'none',
    }}
  />
);

const CHIP_BASE = {
  height: 'auto',
  minHeight: 36,
  borderRadius: 9999,
  fontWeight: 700,
  fontSize: 12,
  color: C.ink,
  '& .MuiChip-label': { px: 1.25, py: 0.75 },
} as const;

interface ChipLook {
  bg: string;
  border: string;
  /** Ink-muted for the states that mean "nothing has happened here". */
  muted?: boolean;
}

const NEUTRAL: ChipLook = { bg: C.bg, border: C.border, muted: true };
const PLAIN: ChipLook = { bg: '#FFFFFF', border: C.border };
const WAIVED: ChipLook = { bg: AMBER.strongBg, border: AMBER.strongBorder };
const COBALT: ChipLook = { bg: '#FFFFFF', border: C.cobalt };

/**
 * One row's current state, per §11's binding table. Everything an admin reads
 * off a roster row at a glance comes through here.
 *
 * The chip always describes this session's mark. A visit elsewhere is shown
 * separately as context and never replaces "Not marked".
 */
export const MarkStateChip: React.FC<{
  line: AttendanceLine;
}> = ({ line }) => {
  const { label, look, dot } = describeMarkState(line);
  return (
    // `span`, because every one of these renders inside a row <button>.
    <Chip
      component="span"
      size="small"
      icon={
        dot === 'none' ? undefined : (
          <Box component="span" sx={{ display: 'inline-flex', pl: 1 }}>
            <StateDot ring={dot === 'ring'} />
          </Box>
        )
      }
      label={label}
      sx={{
        ...CHIP_BASE,
        bgcolor: look.bg,
        border: `1px solid ${look.border}`,
        color: look.muted ? C.inkMuted : C.ink,
        ...TNUM_SX,
      }}
    />
  );
};

export function describeMarkState(line: AttendanceLine): {
  label: string;
  look: ChipLook;
  dot: 'filled' | 'ring' | 'none';
} {
  switch (line.status) {
    case 'present-billed':
      return { label: 'Billed', look: PLAIN, dot: 'filled' };
    case 'absent-billed':
      return { label: 'Absent · billed', look: PLAIN, dot: 'ring' };
    case 'waived':
      return { label: 'Waived', look: WAIVED, dot: 'none' };
    case 'trial-attended':
      return { label: 'Trial · attended', look: COBALT, dot: 'none' };
    case 'trial-no-show':
      return { label: 'Trial · no-show', look: NEUTRAL, dot: 'none' };
    default:
      return { label: 'Not marked', look: PLAIN, dot: 'none' };
  }
}

// ---------------------------------------------------------------------------
// The money consequence the `n/N marked` counter cannot carry
// ---------------------------------------------------------------------------

/**
 * Marking is finished and the session is still worth nothing: no billed mark
 * and no trial attended, so `shared/teacherPay` prices it at $0
 * (`basis: 'no-billable-attendance'`) and no cycle consumes a slot.
 *
 * This exists because `n/N marked` is deliberately a PROGRESS figure — §11 ties
 * it to session completeness and the statement's finalize blockers read the
 * same rule, so a waived line has to count as marked or a fully waived session
 * could never be completed and the month could never be finalized. The cost of
 * that (correct) choice is that one waived student reads "1/1 marked ✓", which
 * looks like a normal taught session. Naming the zero is the fix; moving the
 * counter is not.
 *
 * Gated on `complete`, because an unmarked session also has zero billed and
 * announcing $0 before anyone has marked it would be a guess, not a fact.
 */
export function hasNoBillableAttendance(
  session: Pick<
    AttendanceSessionSummary,
    'status' | 'held' | 'complete' | 'counts'
  >
): boolean {
  return (
    session.status === 'active' &&
    session.held &&
    session.complete &&
    session.counts.billed === 0 &&
    session.counts.trialAttended === 0
  );
}

/** Distinct Zoom names before the room counts as "the class ran" — a lone host does not. */
export const ZOOM_ROOM_RAN_MIN_JOINED = 2;

export function zoomShowsSessionRan(
  snapshot: ZoomParticipantSnapshot | null
): boolean {
  return (
    snapshot?.status === 'ready' &&
    snapshot.joinedCount >= ZOOM_ROOM_RAN_MIN_JOINED
  );
}

/** The home-class provenance chip on a visiting row (cobalt = informational). */
export const HomeClassChip: React.FC<{ classCode: string }> = ({
  classCode,
}) => (
  <Chip
    component="span"
    size="small"
    label={classCode}
    sx={{
      height: 22,
      borderRadius: 9999,
      bgcolor: '#FFFFFF',
      border: `1.5px solid ${C.cobalt}`,
      color: C.cobaltDark,
      fontWeight: 800,
      fontSize: 11,
      letterSpacing: '0.04em',
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

/**
 * "5 came · 3 absent" — what a teacher said, in the words they said it in.
 *
 * Words rather than the `7✓ 1✗` this first shipped as: Sora carries no ✓ or ✗,
 * so both fell back to whatever the OS had and rendered as a thin serif pair
 * inside an 800-weight chip. A tally an admin is about to turn into money is
 * also the wrong place to make someone decode a glyph.
 */
export function formatDeclarationTally(
  present: number,
  absent: number
): string {
  return `${present} came · ${absent} absent`;
}

/**
 * "Teacher declared" — the inbox's signal that somebody has already said what
 * happened in the room. A badge and nothing else: the counts ride beside it as
 * ink, where they can wear `tnum` and stay legible.
 *
 * Cobalt outline, the informational family (`HomeClassChip`'s look), and
 * deliberately NOT green or amber: a declaration is input, not a settled state,
 * and dressing it as one would have an admin reading a marked session off a row
 * where nothing has been marked.
 */
export const DeclarationChip: React.FC = () => (
  <Chip
    component="span"
    size="small"
    label="Teacher declared"
    sx={{
      height: 22,
      borderRadius: 9999,
      bgcolor: 'background.paper',
      border: `1.5px solid ${C.cobalt}`,
      color: C.cobaltDark,
      fontWeight: 800,
      fontSize: 11,
      letterSpacing: '0.04em',
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

/**
 * Session-level status, for the inbox rows and the marking header. Cancelled
 * and superseded are audit states — never styled as if they invite a tap.
 */
export const SessionStateChip: React.FC<{
  status: 'active' | 'cancelled' | 'superseded';
  locked: boolean;
}> = ({ status, locked }) => {
  if (status === 'cancelled') {
    return (
      <Chip
        component="span"
        size="small"
        label="Cancelled"
        sx={{
          ...CHIP_BASE,
          bgcolor: C.bgAlt,
          border: `1px solid ${C.border}`,
          color: C.inkMuted,
        }}
      />
    );
  }
  if (status === 'superseded') {
    return (
      <Chip
        component="span"
        size="small"
        label="Superseded"
        sx={{
          ...CHIP_BASE,
          bgcolor: 'transparent',
          border: `1px dashed ${C.border}`,
          color: C.inkMuted,
        }}
      />
    );
  }
  if (locked) {
    return (
      <Chip
        component="span"
        size="small"
        label="Finalized"
        sx={{
          ...CHIP_BASE,
          bgcolor: C.bgAlt,
          border: `1px solid ${C.border}`,
          color: C.inkMuted,
        }}
      />
    );
  }
  return null;
};

/** "RELIEF · Ms Chua" — amber-wash, because relief changes who gets paid. */
export const ReliefChip: React.FC<{ teacherName: string | null }> = ({
  teacherName,
}) => (
  <Chip
    component="span"
    size="small"
    label={teacherName ? `RELIEF · ${teacherName}` : 'RELIEF'}
    sx={{
      height: 22,
      borderRadius: 9999,
      bgcolor: AMBER.strongBg,
      border: `1px solid ${AMBER.strongBorder}`,
      color: C.ink,
      fontWeight: 800,
      fontSize: 11,
      letterSpacing: '0.04em',
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

/**
 * Every 4xx the attendance routes speak, turned into copy an admin can act on.
 * Keyed off `code`, never `message`: the same `session_superseded` code carries
 * different server prose per route, and the guidance below is what actually
 * differs to the person reading it.
 */
export function attendanceErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  switch (code) {
    case 'session_version_conflict':
      return 'Someone else updated this attendance — row refreshed.';
    case 'session_finalized':
      return 'This month is finalized. Fixes become corrections on the next statement.';
    case 'session_superseded':
      return 'This session was replaced by a rescheduled one — it is kept for audit only.';
    case 'session_cancelled':
      return 'This session is cancelled, so nobody is billed and nobody is paid.';
    case 'session_not_held':
      return 'Attendance opens once the class has started.';
    case 'session_has_marks':
      return 'Clear the marks on this session before cancelling it.';
    case 'student_already_on_session':
      return 'That student is already on this session.';
    case 'home_class_not_a_membership':
      return 'That student is not an active member of the class you picked.';
    case 'home_class_not_found':
      return 'That home class is not a live class.';
    case 'mark_status_kind_mismatch':
      return 'That mark does not apply to this kind of student.';
    case 'mark_line_not_found':
    case 'visiting_mark_not_found':
      return 'That student is no longer on this session — refreshing.';
    case 'move_target_in_future':
      return 'A session can only be moved to a date that has already passed.';
    // Reachable by URL: a nonprofit assignment group has a class page but no
    // sessions, and neither does a deleted class code.
    case 'class_not_a_live_class':
      return 'This class code is an assignment group, not a live class — it has no sessions.';
    case 'class_not_found':
      return 'That class no longer exists.';
    // The session outlived its class code and never took a mark, so there is no
    // roster left to project onto the marking screen.
    case 'class_code_deleted':
      return 'This class code was deleted, so there is no roster to mark. Cancel the session instead.';
    default:
      return err instanceof Error ? err.message : 'Something went wrong';
  }
}

/** A 409 means our snapshot is stale; the caller must refetch, not retry. */
export function isVersionConflict(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'session_version_conflict';
}
